import type { ReactElement } from "react";

import type { MedicalReviewState } from "@/constants/Onboarding";
import { TextTestID } from "@/constants/TestID";
import { TextSeparator } from "@/constants/Text";
import { getOnboardingHeaderContent } from "@/ui/components/onboarding/v2/content/OnboardingHeaderContent";
import { Avatar, ProgressBar, Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface OnboardingProgressCardProps extends ViewWithTestID {
  currentStepIndex: number;
  medicalReviewState?: MedicalReviewState;
  patientName?: string;
  totalSteps: number;
}

const OnboardingProgressCard = ({
  currentStepIndex,
  medicalReviewState,
  patientName,
  testID,
  totalSteps,
}: OnboardingProgressCardProps): ReactElement => {
  const { counter, subtitle } = getOnboardingHeaderContent({
    currentStepIndex,
    medicalReviewState,
    patientName,
  });

  const baseTestID = joinTestIDs(testID, "progress");

  return (
    <VStack gap={12} testID={baseTestID}>
      <ProgressBar
        currentStep={currentStepIndex + 1}
        testID={baseTestID}
        totalSteps={totalSteps}
      />
      <HStack alignItems="center" gap={10}>
        <Avatar
          size="xs"
          testID={baseTestID}
          userName="Equip"
          userType="provider"
        />
        <Box flex={1}>
          <Text
            color={ColorToken.NeutralSecondary}
            testID={joinTestIDs(baseTestID, TextTestID.Subtitle)}
            variant="caption"
          >
            {counter && (
              <>
                <Text
                  color={ColorToken.NeutralPrimary}
                  testID={joinTestIDs(baseTestID, "counter")}
                  variant="captionMedium"
                >
                  {counter}
                </Text>
                {` ${TextSeparator.Dot} `}
              </>
            )}
            {subtitle}
          </Text>
        </Box>
      </HStack>
    </VStack>
  );
};

export type { OnboardingProgressCardProps };
export { OnboardingProgressCard };
